import { useCallback, useEffect, useMemo, useState } from 'react';
import Pager, { usePaged } from '../../components/Pager';
import { auditService } from '../../services/auditService';
import { fmtQty } from '../Reports/ui';
import { PageLoader } from '../../components/Loader';
import './LocationBatches.css';

const SIZES = [
  { value: 0, label: 'One batch per zone' },
  { value: 10, label: 'Up to 10 locations' },
  { value: 15, label: 'Up to 15 locations' },
  { value: 20, label: 'Up to 20 locations' },
  { value: 25, label: 'Up to 25 locations' },
  { value: 30, label: 'Up to 30 locations' },
  { value: 50, label: 'Up to 50 locations' },
];

function when(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const BATCH_FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'left', label: 'To go', test: (b) => b.left > 0 },
  { id: 'done', label: 'Done', test: (b) => b.total > 0 && b.left === 0 },
  { id: 'free', label: 'Not given', test: (b) => !b.assigned_user_id },
];

/** The location codes of one opened batch, 100 at a time. */
function BatchCodes({ locations }) {
  const paged = usePaged(locations, { pageSize: 100, sizes: [100, 200, 500] });
  return (
    <>
      <div className="loc-batch__codes">
        {paged.items.map((location) => (
          <span key={location.code} className={location.done ? 'loc-batch__code--done' : ''} title={location.area || ''}>
            {location.code}
          </span>
        ))}
      </div>
      <Pager paged={paged} noun="locations" compact />
    </>
  );
}

/**
 * Batches of the location master (by zone; big zones split into parts) and
 * which member scans each one. Members are optional: the team lead sets them
 * on the phone, Admin / Data ASA can change them here.
 */
function LocationBatches({ auditId, canManage, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [size, setSize] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const shown = useMemo(() => {
    const list = data?.batches || [];
    const test = (BATCH_FILTERS.find((f) => f.id === filter) || BATCH_FILTERS[0]).test;
    const term = search.trim().toLowerCase();
    return list.filter(
      (b) =>
        test(b) &&
        (!term ||
          [b.name, b.first_code, b.last_code, b.areas, b.assigned_user_name, `#${b.batch_no}`]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(term) ||
          (b.locations || []).some((l) => String(l.code).toLowerCase().includes(term))),
    );
  }, [data, filter, search]);
  const paged = usePaged(shown, { pageSize: 10, sizes: [10, 20, 50], resetKey: `${filter}|${search}`, storageKey: 'portal-batches' });

  const load = useCallback(async () => {
    try {
      const result = await auditService.getLocationBatches(auditId, { locations: true });
      setData(result);
      setSize((current) => (current === null ? result.batch_size : current));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const rebuild = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Rebuild the batches? Members stay on batches that keep the same name.')) return;
    setBusy(true);
    setError('');
    try {
      const result = await auditService.generateLocationBatches(auditId, size);
      setNotice(`${fmtQty(result.batches)} batches made${result.grouped_by === 'area' ? ' (by area: the location master has no zones)' : ''}.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const assign = async (batch, value) => {
    setError('');
    try {
      await auditService.assignLocationBatch(auditId, batch.id, value ? Number(value) : null);
      setNotice(value ? `${batch.name} given to ${data.members.find((m) => String(m.id) === value)?.name}.` : `${batch.name} has no member now.`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!data) {
    return error ? <p className="compare-error">{error}</p> : <PageLoader label="Loading batches…" compact />;
  }

  const { summary, batches, members } = data;
  if (!summary.locations) return null;

  return (
    <section className="loc-batches" aria-label="Location batches">
      <header className="loc-batches__head">
        <div>
          <h3>Batches</h3>
          <p className="live-muted">
            {fmtQty(summary.locations)} locations · <b>{fmtQty(summary.done)} done</b> · {fmtQty(summary.left)} to go ·{' '}
            {fmtQty(summary.batches)} batches, {fmtQty(summary.assigned)} given to a member
          </p>
        </div>
        {canManage ? (
          <div className="loc-batches__controls">
            <label>
              Batch size
              <select value={size ?? 0} onChange={(event) => setSize(Number(event.target.value))} disabled={busy}>
                {SIZES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="compare-button compare-button--ghost" onClick={rebuild} disabled={busy}>
              {busy ? 'Rebuilding…' : 'Rebuild batches'}
            </button>
          </div>
        ) : null}
      </header>
      {error ? <p className="compare-error">{error}</p> : null}
      {notice ? <p className="live-notice">{notice}</p> : null}
      {canManage && !members.length ? (
        <p className="live-muted">
          No members on this schedule yet: add teams to the schedule to give batches to people.
        </p>
      ) : null}

      {batches.length > 5 ? (
        <div className="loc-batches__filters">
          <div className="live-chips" role="group" aria-label="Show batches">
            {BATCH_FILTERS.map((f) => (
              <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
                {f.label} ({fmtQty(batches.filter(f.test).length)})
              </button>
            ))}
          </div>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search batch, location code or member…"
            aria-label="Search batches"
          />
        </div>
      ) : null}

      <div className="loc-batches__list" ref={paged.anchorRef}>
        {!shown.length ? <p className="live-muted">No batch matches.</p> : null}
        {paged.items.map((batch) => {
          const pct = batch.total ? Math.round((batch.done / batch.total) * 100) : 0;
          const expanded = open === batch.id;
          return (
            <article key={batch.id} className={`loc-batch${batch.left === 0 && batch.total ? ' loc-batch--done' : ''}`}>
              <div className="loc-batch__main">
                <div className="loc-batch__name">
                  <span className="loc-batch__no">#{batch.batch_no}</span>
                  <strong>{batch.name}</strong>
                  <small>
                    {batch.first_code}
                    {batch.last_code && batch.last_code !== batch.first_code ? ` – ${batch.last_code}` : ''}
                    {batch.areas ? ` · ${batch.areas}` : ''}
                  </small>
                </div>
                <div className="loc-batch__progress">
                  <span>
                    <b>{fmtQty(batch.done)}</b> / {fmtQty(batch.total)} done
                    {batch.left ? ` · ${fmtQty(batch.left)} to go` : ' ✓'}
                    {batch.gc_qty !== null ? ` · GC ${fmtQty(batch.gc_qty)}` : ''}
                  </span>
                  <div className="live-bar live-bar--thin">
                    <div style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="loc-batch__member">
                  {canManage ? (
                    <select
                      aria-label={`Member for ${batch.name}`}
                      value={batch.assigned_user_id ? String(batch.assigned_user_id) : ''}
                      onChange={(event) => assign(batch, event.target.value)}
                    >
                      <option value="">Not given to anyone</option>
                      {members.map((member) => (
                        <option key={member.id} value={String(member.id)}>
                          {member.name} ({member.role})
                        </option>
                      ))}
                      {batch.assigned_user_id && !members.some((m) => m.id === batch.assigned_user_id) ? (
                        <option value={String(batch.assigned_user_id)}>{batch.assigned_user_name} (not on the schedule)</option>
                      ) : null}
                    </select>
                  ) : (
                    <span>{batch.assigned_user_name || 'Not given to anyone'}</span>
                  )}
                  {batch.assigned_by_name ? (
                    <small>
                      by {batch.assigned_by_name} {when(batch.assigned_at)}
                    </small>
                  ) : null}
                </div>
                <button type="button" className="compare-link" onClick={() => setOpen(expanded ? null : batch.id)} aria-expanded={expanded}>
                  {expanded ? 'Hide locations' : 'Locations'}
                </button>
              </div>
              {expanded ? <BatchCodes locations={batch.locations || []} /> : null}
            </article>
          );
        })}
      </div>
      <Pager paged={paged} noun="batches" compact />
    </section>
  );
}

export default LocationBatches;
